import { useRef, type PointerEvent } from 'react'

import { cn } from '@/lib/utils'

type PanelSize = { width: number; height: number }

export function ResizeCorner({
  size,
  onChange,
  className,
  measure,
}: {
  size: PanelSize
  onChange: (size: PanelSize) => void
  className?: string
  measure?: () => PanelSize | undefined
}) {
  const drag = useRef<
    { pointerId: number; x: number; y: number; size: PanelSize } | null
  >(null)

  const start = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      size: measure?.() ?? size,
    }
  }

  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    onChange({
      width: drag.current.size.width + (event.clientX - drag.current.x),
      height: drag.current.size.height + (event.clientY - drag.current.y),
    })
  }

  const finish = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    drag.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      data-slot="resize-corner"
      aria-hidden="true"
      className={cn(
        'group absolute right-0 bottom-0 z-30 hidden size-4 cursor-nwse-resize touch-none md:block',
        className,
      )}
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={finish}
      onPointerCancel={finish}
    >
      <span
        className="border-muted-foreground/40 group-hover:border-ring/70 absolute right-1 bottom-1 size-2 border-r-2 border-b-2 transition-colors"
      />
    </div>
  )
}
